/**
 * SONE core — reading a formula (ADR-0056).
 *
 * Hand-written recursive descent, a few hundred lines, bounded in input and in
 * depth. No `Function`, no `eval`, no library: this parses strings out of a
 * document and the result is evaluated on the server, so a formula language is a
 * sandbox escape waiting to be found if it is anything other than a small parser
 * that does exactly what it says.
 *
 * In core because both sides need it: the server evaluates, and the interface
 * resolves field names to ids when a formula is saved.
 */
export const MAX_FORMULA = 500;
const MAX_DEPTH = 32;
/**
 * The functions a formula may call.
 *
 * A small set, and adding to it is a decision each time rather than a namespace.
 * Arity is checked here so the evaluator never has to guess what a call meant.
 */
export const FUNCTIONS = {
    if: { min: 3, max: 3 },
    round: { min: 1, max: 2 },
    abs: { min: 1, max: 1 },
    min: { min: 1, max: 8 },
    max: { min: 1, max: 8 },
    concat: { min: 1, max: 8 },
    length: { min: 1, max: 1 },
    lower: { min: 1, max: 1 },
    upper: { min: 1, max: 1 },
    contains: { min: 2, max: 2 },
    coalesce: { min: 2, max: 8 },
    /** Whole days from the first date to the second. Negative when it is earlier. */
    days: { min: 2, max: 2 },
    /** Today, so a formula can say how old something is. */
    today: { min: 0, max: 0 },
    isblank: { min: 1, max: 1 },
};
const OPERATORS = ['<=', '>=', '<>', '+', '-', '*', '/', '(', ')', ',', '=', '<', '>'];
function tokenise(text) {
    const tokens = [];
    let at = 0;
    while (at < text.length) {
        const char = text[at];
        if (/\s/.test(char)) {
            at += 1;
            continue;
        }
        if (/[0-9]/.test(char) || (char === '.' && /[0-9]/.test(text[at + 1] ?? ''))) {
            const match = /^[0-9]*\.?[0-9]+/.exec(text.slice(at));
            tokens.push({ kind: 'number', value: Number(match[0]), at });
            at += match[0].length;
            continue;
        }
        if (char === '"') {
            const end = text.indexOf('"', at + 1);
            if (end === -1)
                return { code: 'unclosed_string', at };
            tokens.push({ kind: 'string', value: text.slice(at + 1, end), at });
            at = end + 1;
            continue;
        }
        if (char === '[') {
            const end = text.indexOf(']', at + 1);
            if (end === -1)
                return { code: 'unclosed_bracket', at };
            tokens.push({ kind: 'field', value: text.slice(at + 1, end).trim(), at });
            at = end + 1;
            continue;
        }
        // Letters, including anything a German column name contains: a name is
        // whatever is not an operator, a digit or a space, which is the only rule
        // that does not accidentally exclude somebody's language.
        if (/[\p{L}_]/u.test(char)) {
            const match = /^[\p{L}\p{N}_]+/u.exec(text.slice(at));
            tokens.push({ kind: 'name', value: match[0], at });
            at += match[0].length;
            continue;
        }
        const operator = OPERATORS.find((op) => text.startsWith(op, at));
        if (operator) {
            tokens.push({ kind: 'op', value: operator, at });
            at += operator.length;
            continue;
        }
        return { code: 'unexpected_token', at, found: char };
    }
    tokens.push({ kind: 'end', at: text.length });
    return tokens;
}
// --- the grammar -----------------------------------------------------------
/**
 * Precedence, lowest first — the shape everybody expects from a spreadsheet:
 * `or` < `and` < comparison < `+ -` < `* /` < unary < primary.
 */
export function parseFormula(text) {
    if (text.length > MAX_FORMULA)
        return { error: { code: 'formula_too_long', at: MAX_FORMULA } };
    const tokens = tokenise(text);
    if (!Array.isArray(tokens))
        return { error: tokens };
    let index = 0;
    let depth = 0;
    let failure = null;
    const peek = () => tokens[index] ?? { kind: 'end', at: text.length };
    const isOp = (value) => {
        const token = peek();
        return token.kind === 'op' && token.value === value;
    };
    const isWord = (value) => {
        const token = peek();
        return token.kind === 'name' && token.value.toLowerCase() === value;
    };
    const fail = (error) => {
        failure ??= error;
        return { kind: 'blank' };
    };
    const binary = (ops, next, words = false) => {
        let left = next();
        for (;;) {
            const token = peek();
            const matched = words
                ? token.kind === 'name' && ops.includes(token.value.toLowerCase())
                : token.kind === 'op' && ops.includes(token.value);
            if (!matched)
                return left;
            const op = token.value.toLowerCase();
            index += 1;
            const right = next();
            left = { kind: 'binary', op: op, left, right };
        }
    };
    const primary = () => {
        if (failure)
            return { kind: 'blank' };
        depth += 1;
        if (depth > MAX_DEPTH)
            return fail({ code: 'formula_too_deep', at: peek().at });
        try {
            const token = peek();
            if (token.kind === 'number') {
                index += 1;
                return { kind: 'number', value: token.value };
            }
            if (token.kind === 'string') {
                index += 1;
                return { kind: 'text', value: token.value };
            }
            if (token.kind === 'field') {
                index += 1;
                return { kind: 'field', name: token.value };
            }
            if (token.kind === 'name') {
                const lower = token.value.toLowerCase();
                if (lower === 'true' || lower === 'false') {
                    index += 1;
                    return { kind: 'boolean', value: lower === 'true' };
                }
                if (lower === 'not') {
                    index += 1;
                    return { kind: 'unary', op: 'not', operand: primary() };
                }
                index += 1;
                // A name followed by `(` is a call; otherwise it is a column, which is
                // what makes `Menge * Preis` read the way somebody writes it.
                if (isOp('(')) {
                    index += 1;
                    const args = [];
                    if (!isOp(')')) {
                        for (;;) {
                            args.push(expression());
                            if (isOp(',')) {
                                index += 1;
                                continue;
                            }
                            break;
                        }
                    }
                    if (!isOp(')'))
                        return fail({ code: 'unclosed_bracket', at: token.at });
                    index += 1;
                    return { kind: 'call', name: lower, args };
                }
                return { kind: 'field', name: token.value };
            }
            if (isOp('(')) {
                index += 1;
                const inner = expression();
                if (!isOp(')'))
                    return fail({ code: 'unclosed_bracket', at: token.at });
                index += 1;
                return inner;
            }
            if (isOp('-')) {
                index += 1;
                return { kind: 'unary', op: '-', operand: primary() };
            }
            if (token.kind === 'end')
                return fail({ code: 'unexpected_end', at: token.at });
            return fail({ code: 'unexpected_token', at: token.at, found: String(token.value) });
        }
        finally {
            depth -= 1;
        }
    };
    const product = () => binary(['*', '/'], primary);
    const sum = () => binary(['+', '-'], product);
    const comparison = () => binary(['=', '<>', '<', '<=', '>', '>='], sum);
    const conjunction = () => binary(['and'], comparison, true);
    const expression = () => binary(['or'], conjunction, true);
    const expr = expression();
    if (failure)
        return { error: failure };
    if (peek().kind !== 'end') {
        return { error: { code: 'unexpected_token', at: peek().at, found: String(peek().value ?? '') } };
    }
    return { expr };
}
/** Every column a formula names, for resolving them to ids when it is saved. */
export function fieldsUsed(expr) {
    const found = new Set();
    const walk = (node) => {
        switch (node.kind) {
            case 'field':
                found.add(node.name);
                return;
            case 'unary':
                walk(node.operand);
                return;
            case 'binary':
                walk(node.left);
                walk(node.right);
                return;
            case 'call':
                for (const arg of node.args)
                    walk(arg);
                return;
            default:
                return;
        }
    };
    walk(expr);
    return [...found];
}
/**
 * Attach the field ids a formula was saved with (ADR-0056).
 *
 * The stored text still says `Menge`; the binding is what turns that into a
 * column that may since have been renamed. A name with no binding is left
 * unbound, so the evaluator reports it by name rather than treating it as
 * blank — which is the difference between "no column called Preis" and a
 * silently wrong total.
 */
export function bindFormula(expr, idOf) {
    switch (expr.kind) {
        case 'field': {
            const fieldId = idOf(expr.name);
            return fieldId ? { ...expr, fieldId } : expr;
        }
        case 'unary':
            return { ...expr, operand: bindFormula(expr.operand, idOf) };
        case 'binary':
            return {
                ...expr,
                left: bindFormula(expr.left, idOf),
                right: bindFormula(expr.right, idOf),
            };
        case 'call':
            return { ...expr, args: expr.args.map((arg) => bindFormula(arg, idOf)) };
        default:
            return expr;
    }
}
/**
 * What could complete the word being typed (ADR-0056).
 *
 * In core beside the parser, not in the dialog: the list of what a formula may
 * name is the parser's business, and a second list in the interface would be a
 * second answer to "what is allowed" — one that could offer a function the
 * evaluator does not have.
 *
 * The word is taken from the caret backwards, and only letters count. So a
 * caret after `Menge * Pr` completes `Pr`, and one after `Menge * ` completes
 * nothing rather than everything: a list that appears when somebody has typed
 * no word yet is a list they have to dismiss.
 */
export function completions(text, caret, columns) {
    const before = text.slice(0, caret);
    const word = /[\p{L}_][\p{L}\p{N}_]*$/u.exec(before)?.[0] ?? '';
    if (word === '')
        return [];
    const lower = word.toLowerCase();
    const out = [];
    for (const name of columns) {
        if (name.toLowerCase().startsWith(lower))
            out.push({ value: name, kind: 'column' });
    }
    for (const name of Object.keys(FUNCTIONS)) {
        if (name.startsWith(lower))
            out.push({ value: name, kind: 'function' });
    }
    // Columns first: a formula is mostly about this collection's own fields, and
    // a function list that pushes them below the fold is a list about the
    // language rather than about the data.
    return out.slice(0, 8);
}
/** Replace the word at the caret, and say where the caret goes next. */
export function applyCompletion(text, caret, value, kind) {
    const before = text.slice(0, caret);
    const word = /[\p{L}_][\p{L}\p{N}_]*$/u.exec(before)?.[0] ?? '';
    const start = caret - word.length;
    // A column with a space needs its brackets, and a function needs its
    // parenthesis — inserting the name alone would leave something that does not
    // parse, which is a completion that made the formula worse.
    const inserted = kind === 'function' ? `${value}(` : /\s/.test(value) ? `[${value}]` : value;
    return {
        text: text.slice(0, start) + inserted + text.slice(caret),
        caret: start + inserted.length,
    };
}
//# sourceMappingURL=parse.js.map